import { List } from '@material-ui/core'
import {
  IconAdd,
  IconAirfare,
  IconBills,
  IconCreditCard,
  IconMeals,
  IconPayment,
  IconRequest,
  MenuListItem,
  WithPopover,
} from '@procurify/ui'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { useModuleConfig } from '@webapp/app/config/useModuleConfig'
import { BridgeLink } from '@webapp/core/components/BridgeLink'
import {
  makeSelectFeatureSwitches,
  makeSelectNavigationPermissions,
} from '@webapp/core/stores/global'
import {
  useProcurifyIntl,
  useFeatureFlag,
  useBillsPermissions,
} from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { getNewBillModalUrl } from '@webapp/modules/accounts-payable/features/upload-bills-for-extraction/utils'
import { OrderRequestLink } from '@webapp/modules/purchasing/components/OrderRequestLink'
import { QuickRequestGuidedIntakePromo } from './QuickRequestGuidedIntakePromo'
import messages from '../messages'
import { QuickRequestButtonStyled } from '../styles/QuickRequestButtonStyled'

export const QuickRequestButton = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const ARCH_544_MIGRATION_EXPENSE_REQUEST = useFeatureFlag(
    'ARCH_544_MIGRATION_EXPENSE_REQUEST'
  )
  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')
  const AP_10538_INVOICE_INTAKE_BY_UPLOAD = useFeatureFlag(
    'AP_10538_INVOICE_INTAKE_BY_UPLOAD'
  )
  const DATA_1439_GLOBAL_SEARCH = useFeatureFlag('DATA_1439_GLOBAL_SEARCH')
  const DATA_2061_GUIDED_INTAKE_ORDERS = useFeatureFlag(
    'DATA_2061_GUIDED_INTAKE_ORDERS'
  )
  const permissions = useSelector(makeSelectNavigationPermissions())

  const { enable_airwallex_issuing: isAirwallexEnabled } = useSelector(
    makeSelectFeatureSwitches()
  )

  const { quickRequestButton: entitlement } = useModuleConfig()

  const { canEditPendingDraftDeniedBills } = useBillsPermissions()

  const canRequestOrder =
    permissions.includes('request__order') && entitlement.enableOrderButton
  const canRequestExpense =
    permissions.includes('request__expense') && entitlement.enableExpenseButton
  const canRequestTravel =
    permissions.includes('request__travel') && entitlement.enableTravelButton
  const canRequestFund =
    isAirwallexEnabled &&
    permissions.includes('request__pay_request') &&
    entitlement.enableFundButton
  const canRequestVirtualCard =
    isAirwallexEnabled &&
    permissions.includes('pay__virtual_cards') &&
    entitlement.enableVirtualCardButton
  // The navigation permission does not imply the user can create a bill. The
  // expense bill link goes to unbilled expenses instead and keeps its existing
  // gating.
  const canRequestBill =
    permissions.includes('ap__create_bills') &&
    (entitlement.enableExpenseBillButton || canEditPendingDraftDeniedBills)

  const showGuidedIntakePromo =
    DATA_1439_GLOBAL_SEARCH && DATA_2061_GUIDED_INTAKE_ORDERS && canRequestOrder

  const quickRequestBillButton = entitlement.enableExpenseBillButton
    ? {
        title: intl.formatMessage(messages.quickRequestExpenseBillBtnTitle),
        text: intl.formatMessage(messages.quickRequestExpenseBillBtnText),
        to: url('LegacyUnbilledExpenseItemsPage'),
      }
    : {
        title: intl.formatMessage(messages.quickRequestBillBtnTitle),
        text: intl.formatMessage(messages.quickRequestBillBtnText),
        // Creating a bill starts with the upload modal on the manage bills
        // page instead of the create bill page.
        to: AP_10538_INVOICE_INTAKE_BY_UPLOAD
          ? getNewBillModalUrl(url)
          : url('BillCreatePage', {}),
      }

  return canRequestOrder ||
    canRequestExpense ||
    canRequestTravel ||
    canRequestFund ||
    canRequestVirtualCard ||
    canRequestBill ? (
    <WithPopover
      content={({ dismiss }) => (
        <>
          <List
            style={{
              maxHeight: '230px',
              overflowX: 'hidden',
              overflowY: 'auto',
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
            }}
          >
            {canRequestOrder && (
              <OrderRequestLink
                title={intl.formatMessage(messages.quickRequestOrderBtnTitle)}
                onClick={dismiss}
              >
                <MenuListItem
                  icon={<IconRequest />}
                  text={intl.formatMessage(messages.quickRequestOrderBtnText)}
                />
              </OrderRequestLink>
            )}
            {canRequestExpense && (
              <BridgeLink
                to={
                  ARCH_544_MIGRATION_EXPENSE_REQUEST
                    ? url('ExpenseRequestPage')
                    : '/#/expenses/request'
                }
                onClick={dismiss}
              >
                <MenuListItem
                  icon={<IconMeals />}
                  text={intl.formatMessage(messages.quickRequestExpenseBtnText)}
                />
              </BridgeLink>
            )}
            {canRequestTravel && (
              <BridgeLink
                to={
                  PSERV_346_BASE_MIGRATION
                    ? url('TravelRequestPage')
                    : '/#/travels/request'
                }
                title={intl.formatMessage(messages.quickRequestTravelBtnTitle)}
                onClick={dismiss}
              >
                <MenuListItem
                  icon={<IconAirfare />}
                  text={intl.formatMessage(messages.quickRequestTravelBtnText)}
                />
              </BridgeLink>
            )}
            {canRequestVirtualCard && (
              <Link
                to={
                  isAirwallexEnabled
                    ? url('RequestVirtualCardPage')
                    : url('SpendingCardRequestVirtualCardPage')
                }
                title={intl.formatMessage(
                  messages.quickRequestVirtualCardBtnTitle
                )}
                onClick={dismiss}
              >
                <MenuListItem
                  icon={<IconCreditCard />}
                  text={intl.formatMessage(
                    messages.quickRequestVirtualCardBtnText
                  )}
                />
              </Link>
            )}
            {canRequestFund && (
              <Link
                to={url('SpendingCardCreateFundRequestPage')}
                title={intl.formatMessage(
                  messages.quickRequestPhysicalCardFundBtnTitle
                )}
                onClick={dismiss}
              >
                <MenuListItem
                  icon={<IconPayment />}
                  text={intl.formatMessage(
                    messages.quickRequestPhysicalCardFundBtnText
                  )}
                />
              </Link>
            )}
            {canRequestBill && (
              <Link
                to={quickRequestBillButton.to}
                title={quickRequestBillButton.title}
                onClick={dismiss}
              >
                <MenuListItem
                  icon={<IconBills size={16} />}
                  text={quickRequestBillButton.text}
                />
              </Link>
            )}
          </List>
          {showGuidedIntakePromo && (
            <QuickRequestGuidedIntakePromo onDismissMenu={dismiss} />
          )}
        </>
      )}
      trigger='click'
      placement='bottom'
      TooltipProps={{
        minWidth: 0,
      }}
    >
      <QuickRequestButtonStyled
        iconComponent={<IconAdd />}
        className='qa-header-shortcut-button'
      >
        {intl.formatMessage(messages.quickRequestBtnTitle)}
      </QuickRequestButtonStyled>
    </WithPopover>
  ) : null
}
